import React, { useState, useEffect } from 'react';
import { X, CalendarClock, Check, Loader2, Calendar } from 'lucide-react';
import './KeyInformationModal.css';

/**
 * Modal component for updating key project metadata
 */
const KeyInformationModal = ({
    isOpen,
    onClose,
    onSave,
    isUpdating,
    editableData,
    formatForDateInput
}) => {
    const [localData, setLocalData] = useState({
        query_deadline: '',
        proposal_deadline: '',
        valuation: '',
        currency: 'AED'
    });
    const [error, setError] = useState('');

    const today = new Date().toISOString().split('T')[0];

    const toDMY = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('T')[0].split(/[-/]/);
        if (parts.length === 3) {
            if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
        return dateStr;
    };

    const toISO = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split(/[-/]/);
        if (parts.length === 3) {
            if (parts[2].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
        return dateStr;
    };

    const parseDate = (dmyStr) => {
        if (!dmyStr) return null;
        const parts = dmyStr.split(/[-/]/);
        if (parts.length === 3) {
            let day, month, year;
            if (parts[0].length === 4) {
                year = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10) - 1;
                day = parseInt(parts[2], 10);
            } else {
                day = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10) - 1;
                year = parseInt(parts[2], 10);
            }
            return new Date(year, month, day);
        }
        return null;
    };

    const handleDateChange = (field, value) => {
        setError('');
        setLocalData(prev => ({ ...prev, [field]: value }));
    };

    const getQueryDeadlineMax = () => {
        if (!localData.proposal_deadline) return undefined;
        const pISO = toISO(localData.proposal_deadline);
        if (!pISO) return undefined;
        const pDate = new Date(pISO);
        pDate.setDate(pDate.getDate() - 1);
        return pDate.toISOString().split('T')[0];
    };

    const getProposalDeadlineMin = () => {
        if (!localData.query_deadline) return today;
        const qISO = toISO(localData.query_deadline);
        if (!qISO) return today;
        const qDate = new Date(qISO);
        qDate.setDate(qDate.getDate() + 1);
        const qStr = qDate.toISOString().split('T')[0];
        return qStr > today ? qStr : today;
    };

    const formatIndian = (numStr) => {
        if (!numStr) return '';
        let lastThree = numStr.substring(numStr.length - 3);
        let otherDigits = numStr.substring(0, numStr.length - 3);
        if (otherDigits !== '') {
            lastThree = ',' + lastThree;
        }
        return otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
    };

    const formatInternational = (numStr) => {
        if (!numStr) return '';
        return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const formatValuationValue = (value, curr) => {
        if (value === undefined || value === null || value === '') return '';
        const rawVal = String(value).replace(/[^0-9.]/g, '');
        const dotIndex = rawVal.indexOf('.');
        let integerPart = rawVal;
        let decimalPart = '';
        if (dotIndex !== -1) {
            integerPart = rawVal.slice(0, dotIndex);
            decimalPart = rawVal.slice(dotIndex + 1);
        }
        
        let formattedInteger = '';
        if (curr === 'IND') {
            formattedInteger = formatIndian(integerPart);
        } else {
            formattedInteger = formatInternational(integerPart);
        }
        
        let result = formattedInteger;
        if (dotIndex !== -1) {
            result += '.' + decimalPart;
        }
        return result;
    };

    useEffect(() => {
        if (isOpen && editableData) {
            let rawValuation = editableData.VALUATION || editableData.valuation || '';
            let rawCurrency = editableData.CURRENCY || editableData.currency || 'AED';

            if (rawCurrency === 'NOT' || !['AED', 'USD', 'IND', 'EUR', 'SAR'].includes(rawCurrency.toUpperCase())) {
                rawCurrency = 'AED';
            }

            if (typeof rawValuation === 'string') {
                const match = rawValuation.match(/^([A-Z]{2,4})\s+(.*)$/i);
                if (match) {
                    const parsedCurr = match[1].toUpperCase();
                    if (['AED', 'USD', 'IND', 'EUR', 'SAR'].includes(parsedCurr)) {
                        rawCurrency = parsedCurr;
                        rawValuation = match[2];
                    } else {
                        // If it's a word like "Not" or anything else not in our list,
                        // treat the entire string as valuation (after stripping alphabetical prefix)
                        rawValuation = rawValuation.replace(/^[A-Z]{2,4}\s+/i, '').trim();
                    }
                } else {
                    rawValuation = rawValuation.replace(/^[A-Z]{2,4}\s+/i, '').trim();
                }
                rawValuation = rawValuation.replace(/[^0-9,.]/g, '');
            } else if (typeof rawValuation === 'number') {
                rawValuation = String(rawValuation);
            }

            const rawVal = rawValuation.replace(/,/g, '');
            const formattedValuation = formatValuationValue(rawVal, rawCurrency);

            setLocalData({
                query_deadline: toDMY(formatForDateInput(editableData.QUERY_DEADLINE || editableData.query_deadline || '')),
                proposal_deadline: toDMY(formatForDateInput(editableData.PROPOSAL_DEADLINE || editableData.proposal_deadline || '')),
                valuation: formattedValuation,
                currency: rawCurrency
            });
        }
    }, [isOpen, editableData, formatForDateInput]);

    if (!isOpen) return null;

    const handleCurrencyChange = (newCurr) => {
        setLocalData(prev => {
            const rawVal = prev.valuation.replace(/,/g, '');
            return {
                ...prev,
                currency: newCurr,
                valuation: formatValuationValue(rawVal, newCurr)
            };
        });
    };

    const handleValuationChange = (val) => {
        let clean = val.replace(/[^0-9.,]/g, '');
        const parts = clean.split('.');
        if (parts.length > 2) {
            clean = parts[0] + '.' + parts.slice(1).join('');
        }
        
        const raw = clean.replace(/,/g, '');
        const formatted = formatValuationValue(raw, localData.currency);
        setLocalData(prev => ({ ...prev, valuation: formatted }));
    };

    const handleSave = () => {
        setError('');
        
        const qDate = parseDate(localData.query_deadline);
        const pDate = parseDate(localData.proposal_deadline);
        
        if (qDate && pDate && qDate >= pDate) {
            setError('Query Submission date must be earlier than the Proposal Submission date.');
            return;
        }

        const currency = localData.currency || 'AED';
        const rawVal = localData.valuation.replace(/,/g, '').trim();
        const payload = {
            query_deadline: toISO(localData.query_deadline),
            proposal_deadline: toISO(localData.proposal_deadline),
            valuation: rawVal,
            VALUATION: rawVal,
            currency: currency,
            CURRENCY: currency,
            valuation_display: `${currency} ${localData.valuation}`.trim()
        };
        onSave(payload);
    };

    return (
        <div className="ai-modal-overlay">
            <div className="ai-modal-content key-info-modal">
                <div className="ai-modal-header">
                    <div className="ai-modal-title">
                        <CalendarClock size={20} color="#FF5526" />
                        <h3>Update Key Information</h3>
                    </div>
                    <button className="modal-close-btn" onClick={onClose} disabled={isUpdating}>
                        <X size={20} />
                    </button>
                </div>
                <div className="ai-modal-body key-info-modal-body">
                    {error && (
                        <div className="key-info-error-banner">
                            <span>⚠️ {error}</span>
                        </div>
                    )}
                    <div className="modal-row">
                        <div className="modal-field-group">
                            <label htmlFor="query_deadline">QUERY SUBMISSION</label>
                            <div className="smart-date-wrapper">
                                <input
                                    id="query_deadline"
                                    name="query_deadline"
                                    type="text"
                                    className="modal-input smart-input"
                                    value={localData.query_deadline}
                                    onChange={(e) => handleDateChange('query_deadline', e.target.value)}
                                    placeholder="DD-MM-YYYY"
                                    disabled={isUpdating}
                                />
                                <div className="calendar-trigger-wrapper" onClick={(e) => {
                                    try {
                                        e.currentTarget.querySelector('.hidden-native-picker')?.showPicker();
                                    } catch (err) {
                                        console.warn('showPicker not supported:', err);
                                    }
                                }}>
                                    <Calendar size={16} className="calendar-icon-trigger" />
                                    <input 
                                        type="date" 
                                        className="hidden-native-picker"
                                        min={today}
                                        max={getQueryDeadlineMax()}
                                        onChange={(e) => handleDateChange('query_deadline', toDMY(e.target.value))}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-field-group">
                            <label htmlFor="proposal_deadline">PROPOSAL SUBMISSION</label>
                            <div className="smart-date-wrapper">
                                <input
                                    id="proposal_deadline"
                                    name="proposal_deadline"
                                    type="text"
                                    className="modal-input smart-input"
                                    value={localData.proposal_deadline}
                                    onChange={(e) => handleDateChange('proposal_deadline', e.target.value)}
                                    placeholder="DD-MM-YYYY"
                                    disabled={isUpdating}
                                />
                                <div className="calendar-trigger-wrapper" onClick={(e) => {
                                    try {
                                        e.currentTarget.querySelector('.hidden-native-picker')?.showPicker();
                                    } catch (err) {
                                        console.warn('showPicker not supported:', err);
                                    }
                                }}>
                                    <Calendar size={16} className="calendar-icon-trigger" />
                                    <input 
                                        type="date" 
                                        className="hidden-native-picker"
                                        min={getProposalDeadlineMin()}
                                        onChange={(e) => handleDateChange('proposal_deadline', toDMY(e.target.value))}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-field-group">
                        <label htmlFor="valuation">ESTIMATED VALUATION</label>
                        <div className="valuation-composite-input">
                            <div className="currency-badge-prefix-select-wrapper">
                                {/*
                                <select 
                                    value={localData.currency} 
                                    onChange={(e) => handleCurrencyChange(e.target.value)}
                                    className="currency-badge-prefix-select"
                                    disabled={isUpdating}
                                >
                                    <option value="AED">AED</option>
                                    <option value="USD">USD</option>
                                    <option value="IND">IND</option>
                                    <option value="EUR">EUR</option>
                                    <option value="SAR">SAR</option>
                                </select>
                                */}
                                <span style={{ fontWeight: 600, fontSize: '13px', color: '#475569', padding: '0 8px', display: 'flex', alignItems: 'center', height: '100%' }}>AED</span>
                            </div>
                            <input
                                id="valuation"
                                name="valuation"
                                type="text"
                                className="modal-input valuation-input-field"
                                value={localData.valuation}
                                onChange={(e) => handleValuationChange(e.target.value)}
                                placeholder="e.g. 5,000,000"
                                disabled={isUpdating}
                            />
                        </div>
                    </div>

                </div>

                <div className="ai-modal-footer">
                    <button
                        className="primary-action-btn"
                        onClick={handleSave}
                        disabled={isUpdating}
                    >
                        {isUpdating ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        <span>{isUpdating ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KeyInformationModal;


